/* global theme */

/**
 * Discount code entry in the cart page order summary (sections/main-cart.liquid).
 *
 * Uses the Ajax Cart API: POST /cart/update with { discount: 'CODE1,CODE2' } replaces
 * the codes on the cart, and the response lists them in `discount_codes` as
 * { code, applicable }. Sections are requested in the same call (Section Rendering
 * API) and swapped in through <cart-items> (assets/cart-items.js), so the line
 * prices, pills and Order Total update together. The reward cards refresh on their
 * own (assets/progress-bar.js watches /cart/update requests).
 *
 * Status messages (strings in the element's JSON <script>):
 * - empty / comma         input checks before any request
 * - already               the code is already on the cart (no request)
 * - success(Plain)        applied; with the amount it saves when known
 * - replaced              applied, but an existing code stopped applying (removed)
 * - notCombinable         rejected with other codes, but valid on its own
 * - invalid               rejected. Shopify only says "not applicable": it doesn't
 *                         tell a code that doesn't exist from an expired or
 *                         ineligible one, so one short message is used for all.
 * - removed / generic     after × / on a network or server error
 *
 * The summary is re-rendered after every change, so this element is recreated each
 * time; the current codes come from data-codes and a message waiting to be shown
 * after the re-render is kept in CartDiscount.pendingStatus.
 */
if (!customElements.get('cart-discount')) {
  class CartDiscount extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.input = this.querySelector('.cart-discount__input');
      this.applyBtn = this.querySelector('.cart-discount__apply');
      this.statusEl = this.querySelector('.cart-discount__status');

      // Pill × buttons work everywhere, including the cart drawer footer, which
      // only lists the applied discounts (no code field there).
      this.addEventListener('click', this.handleRemoveClick.bind(this));
      if (!this.input || !this.applyBtn) return;

      try {
        this.strings = JSON.parse(this.querySelector('[data-discount-strings]').textContent);
      } catch (e) {
        this.strings = {};
      }
      // Liquid's `t` filter HTML-escapes translations (e.g. &#39;); decode for textContent.
      const decoder = document.createElement('textarea');
      Object.keys(this.strings).forEach((key) => {
        decoder.innerHTML = this.strings[key];
        this.strings[key] = decoder.value;
      });

      this.input.addEventListener('input', this.handleInput.bind(this));
      this.input.addEventListener('keydown', this.handleKeydown.bind(this));
      this.applyBtn.addEventListener('click', () => this.applyCode());

      if (CartDiscount.pendingStatus) {
        const { type, message } = CartDiscount.pendingStatus;
        CartDiscount.pendingStatus = null;
        this.showStatus(type, message, { focus: false });
      }
    }

    /**
     * Enables "Apply" only when there is something to apply.
     */
    handleInput() {
      this.applyBtn.disabled = this.busy || this.input.value.trim() === '';
      if (!this.statusEl.hidden) this.clearStatus();
    }

    /**
     * Enter applies the code. Without this, Enter would submit form#cart (checkout).
     * @param {KeyboardEvent} evt - Event object.
     */
    handleKeydown(evt) {
      if (evt.key !== 'Enter') return;
      evt.preventDefault();
      this.applyCode();
    }

    /**
     * Handles the × button on an applied code pill.
     * @param {MouseEvent} evt - Event object.
     */
    handleRemoveClick(evt) {
      const removeBtn = evt.target.closest('.cart-discount__remove');
      if (!removeBtn) return;
      evt.preventDefault();
      this.removeCode(removeBtn.dataset.code);
    }

    /**
     * Codes currently applied to the cart (rendered by Liquid).
     * @returns {string[]}
     */
    getCodes() {
      return (this.dataset.codes || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }

    async applyCode() {
      if (this.busy) return;
      const code = this.input.value.trim();

      if (!code) {
        this.showStatus('error', this.t('empty'));
        return;
      }
      if (code.includes(',')) {
        this.showStatus('error', this.t('comma'));
        return;
      }

      const currentCodes = this.getCodes();
      const sameAs = (a) => (b) => a.toLowerCase() === b.toLowerCase();

      if (currentCodes.some(sameAs(code))) {
        this.showStatus('info', this.t('already', { code: currentCodes.find(sameAs(code)) }));
        return;
      }

      const data = await this.updateDiscounts([...currentCodes, code]);
      if (!data) return;

      const codes = data.discount_codes || [];
      const result = codes.find((discountCode) => sameAs(code)(discountCode.code));

      if (result && result.applicable) {
        // Codes that applied before but not next to the new one can't be combined with it.
        const dropped = codes.filter((discountCode) => !discountCode.applicable).map((discountCode) => discountCode.code);
        let finalData = data;
        if (dropped.length) {
          const kept = codes.filter((discountCode) => discountCode.applicable).map((discountCode) => discountCode.code);
          finalData = (await this.updateDiscounts(kept)) || data;
        }

        const savedCents = CartDiscount.getCodeSavings(finalData, result.code);
        let message;
        if (dropped.length) {
          message = this.t('replaced', { code: result.code, other: CartDiscount.quoteList(dropped) });
        } else if (savedCents > 0) {
          message = this.t('success', { code: result.code, amount: CartDiscount.formatMoney(savedCents, finalData.currency) });
        } else {
          message = this.t('successPlain', { code: result.code });
        }

        this.input.value = '';
        this.render(finalData, { type: dropped.length ? 'warning' : 'success', message });
        return;
      }

      // Rejected. With other codes on the cart, check whether it works on its own:
      // if so, the problem is the combination, not the code.
      let message = this.t('invalid', { code });
      if (currentCodes.length) {
        const alone = await this.updateDiscounts([code], { render: false });
        const aloneResult = alone && (alone.discount_codes || []).find((discountCode) => sameAs(code)(discountCode.code));
        if (aloneResult && aloneResult.applicable) {
          message = this.t('notCombinable', { code: aloneResult.code, other: CartDiscount.quoteList(currentCodes) });
        }
      }

      // Put the cart back to its previous codes (the rejected one must not linger
      // until checkout). Nothing else changed, so there is nothing to re-render.
      await this.updateDiscounts(currentCodes, { render: false });
      this.showStatus('error', message);
    }

    /**
     * @param {string} code - Code to take off the cart.
     */
    async removeCode(code) {
      if (!code || this.busy) return;
      const codes = this.getCodes().filter((other) => other.toLowerCase() !== code.toLowerCase());
      const data = await this.updateDiscounts(codes);
      if (data) this.render(data, { type: 'info', message: this.t('removed', { code }) });
    }

    /**
     * Replaces the cart's discount codes.
     * @param {string[]} codes - Full list of codes to keep on the cart.
     * @param {object} [options] - { render: false } skips requesting sections.
     * @returns {Promise<?object>} Cart JSON, or null on error (already shown).
     */
    async updateDiscounts(codes, options = {}) {
      const cartItems = document.querySelector('cart-items');
      const withSections = options.render !== false && cartItems && cartItems.getSectionsToRender;
      const body = { discount: codes.join(',') };

      if (withSections) {
        body.sections = [...new Set(cartItems.getSectionsToRender().map((section) => section.section))];
        body.sections_url = cartItems.getSectionsUrl();
      }

      this.setBusy(true);
      try {
        const response = await fetch(theme.routes.cartUpdate, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || response.status);
        return data;
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.showStatus('error', this.t('generic'));
        return null;
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * Swaps in the re-rendered sections (summary, items, cart icon) via <cart-items>,
     * then shows the status message in the new summary.
     * @param {object} data - Cart JSON with `sections`.
     * @param {object} [status] - { type, message } to show after the re-render.
     */
    render(data, status) {
      const cartItems = document.querySelector('cart-items');
      if (status) CartDiscount.pendingStatus = status;

      if (cartItems && cartItems.renderSections && data.sections) {
        cartItems.renderSections(data.sections);
        if (cartItems.updateLiveRegions && cartItems.itemStatus) cartItems.updateLiveRegions();
      } else {
        window.location.reload();
        return;
      }

      // The summary was not re-rendered (e.g. no sections in the response): show it here.
      if (CartDiscount.pendingStatus && this.isConnected) {
        const { type, message } = CartDiscount.pendingStatus;
        CartDiscount.pendingStatus = null;
        this.showStatus(type, message, { focus: false });
      }

      document.dispatchEvent(new CustomEvent('on:cart:discount-change', {
        detail: { cart: data, codes: (data.discount_codes || []).map((discountCode) => discountCode.code) }
      }));
    }

    /**
     * @param {boolean} busy - Whether a request is in flight.
     */
    setBusy(busy) {
      this.busy = busy;
      // Not "is-loading": the theme draws a spinner on every element with that class.
      this.classList.toggle('is-busy', busy);
      this.setAttribute('aria-busy', String(busy));
      if (this.input) this.input.readOnly = busy;
      if (this.applyBtn) this.applyBtn.disabled = busy || !this.input || this.input.value.trim() === '';

      // Same delayed loading state as the line items (cart-items.js#setPageState),
      // on the cart page or, when this sits in the drawer's footer, on the drawer.
      const cartPage = this.closest('[data-cart-page]');
      const cartItems = this.closest('#cart-drawer')
        ? document.querySelector('#cart-drawer cart-items')
        : document.querySelector('[data-cart-page] cart-items');
      if (cartItems && cartItems.setPageState) {
        cartItems.setPageState('is-updating', busy);
      } else if (cartPage) {
        cartPage.classList.toggle('is-updating', busy);
      }
    }

    /**
     * Shows a status message under the field.
     * @param {string} type - 'success' | 'info' | 'warning' | 'error'.
     * @param {string} message - Text to show.
     * @param {object} [options] - { focus: false } keeps focus where it is.
     */
    showStatus(type, message, options = {}) {
      if (!this.statusEl || !message) return;
      this.statusEl.textContent = message;
      this.statusEl.dataset.state = type;
      this.statusEl.setAttribute('role', type === 'error' ? 'alert' : 'status');
      this.statusEl.hidden = false;

      if (type === 'error') {
        this.input.setAttribute('aria-invalid', 'true');
        if (options.focus !== false) this.input.focus();
      } else {
        this.input.removeAttribute('aria-invalid');
      }
    }

    clearStatus() {
      this.statusEl.textContent = '';
      this.statusEl.hidden = true;
      delete this.statusEl.dataset.state;
      this.input.removeAttribute('aria-invalid');
    }

    /**
     * Fills a status string's [placeholders].
     * @param {string} key - String key.
     * @param {object} [values] - Placeholder values.
     * @returns {string}
     */
    t(key, values = {}) {
      return Object.entries(values).reduce(
        (text, [name, value]) => text.split(`[${name}]`).join(value),
        (this.strings && this.strings[key]) || ''
      );
    }

    /**
     * Amount a code takes off the cart, in cents (order and product-level discounts).
     * @param {object} cart - Cart JSON.
     * @param {string} code - Discount code.
     * @returns {number}
     */
    static getCodeSavings(cart, code) {
      const matches = (application) => application
        && application.type === 'discount_code'
        && String(application.title).toLowerCase() === code.toLowerCase();

      const cartLevel = (cart.cart_level_discount_applications || [])
        .filter((entry) => matches(entry.discount_application || entry))
        .reduce((total, entry) => total + Number(entry.amount || entry.total_allocated_amount || 0), 0);

      const lineLevel = (cart.items || []).reduce((total, item) => total
        + (item.line_level_discount_allocations || [])
          .filter((allocation) => matches(allocation.discount_application))
          .reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0), 0);

      return cartLevel + lineLevel;
    }

    /**
     * @param {number} cents - Amount in cents.
     * @param {string} currency - ISO currency code.
     * @returns {string}
     */
    static formatMoney(cents, currency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency || 'USD'
        }).format(cents / 100);
      } catch (e) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    /**
     * @param {string[]} codes - Codes to list.
     * @returns {string} e.g. “A” or “A”, “B”
     */
    static quoteList(codes) {
      return codes.map((code) => `“${code}”`).join(', ');
    }
  }

  CartDiscount.pendingStatus = null;
  customElements.define('cart-discount', CartDiscount);
}
